import { Trash2 } from 'lucide-react'
import type { FaceSummary } from '@/store/api'
import { signedSrc } from '@/utils/media'
import { useSignedUrls } from '@/utils/useSignedUrls'

interface FaceThumbnailStripProps {
  faces: FaceSummary[]
  selectedFaceId: string | null
  onFaceSelect: (face: FaceSummary) => void
  onFaceDelete?: (face: FaceSummary) => void
}

function FaceThumbnailStrip({ faces, selectedFaceId, onFaceSelect, onFaceDelete }: FaceThumbnailStripProps) {
  useSignedUrls()
  if (faces.length === 0) return null

  return (
    <div className="flex items-center gap-2 mt-2">
      {faces.map((face) => {
        const isSelected = selectedFaceId === face.face_id
        return (
          <div key={face.face_id} className="relative group/face flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation()
                onFaceSelect(face)
              }}
              className={`w-10 h-10 rounded-full overflow-hidden border transition-all ${
                isSelected
                  ? 'ring-2 ring-cyan-400 border-cyan-400'
                  : 'border-white/20 hover:border-white/50'
              }`}
              title={[
                face.age != null ? `Age: ~${face.age}` : null,
                face.gender ?? null,
                `${Math.round(face.detection_confidence * 100)}%`,
              ]
                .filter(Boolean)
                .join(' | ')}
            >
              <img
                src={signedSrc(face.crop_url)}
                alt={`Face ${face.face_id}`}
                className="w-full h-full object-cover"
              />
            </button>
            {onFaceDelete && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onFaceDelete(face)
                }}
                className="absolute -top-1 -right-1 p-0.5 rounded-full bg-red-600/80 hover:bg-red-600 text-white opacity-0 group-hover/face:opacity-100 transition-opacity z-10"
                title="Delete this face"
              >
                <Trash2 size={10} />
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default FaceThumbnailStrip
